import ky from 'ky-universal';
import { config } from '../config';

type AccessTokenResponse = {
  token: { access_token: string };
};

export const getAccessToken = async (
  azureAdId: string
): Promise<AccessTokenResponse> => {
  const { teamsBotServiceUrl } = config;

  return await ky
    .get(`${teamsBotServiceUrl}/token`, {
      searchParams: {
        userObjectId: azureAdId,
      },
    })
    .json<AccessTokenResponse>();
};

export type OrganizationSwitchFailure =
  | 'not_member'
  | 'not_supported'
  | 'failed';

// Moves the user's Robin token to another organization. Resolves with the new
// access token, which is bound to that organization; rejects with ky's
// HTTPError otherwise (see classifyOrganizationSwitchError).
export const switchOrganization = async (
  azureAdId: string,
  organizationId: string
): Promise<AccessTokenResponse> => {
  const { teamsBotServiceUrl } = config;

  return await ky
    .post(`${teamsBotServiceUrl}/token/organization`, {
      json: { userObjectId: azureAdId, organizationId },
    })
    .json<AccessTokenResponse>();
};

// Tells apart why a switch was refused: the user is not in that organization
// (403), the OAuth service cannot switch organizations yet (409 not_supported),
// or anything else (network, server error). A 409 conflict means the stored
// token changed while switching (another refresh won the race), so it is
// retryable and reported as a plain failure.
export const classifyOrganizationSwitchError = async (
  error: unknown
): Promise<OrganizationSwitchFailure> => {
  const response = (
    error as { response?: { status?: number; text?: () => Promise<string> } }
  )?.response;

  if (response?.status === 403) return 'not_member';
  if (response?.status === 409) {
    const body = await response.text?.().catch(() => '');
    return body?.includes('conflict') ? 'failed' : 'not_supported';
  }
  return 'failed';
};
